<script setup lang="ts">
/**
 * Step 3 du checkout événement : formulaire payeur (variante "b" sans téléphone).
 */
import type { BuyerInfoModel } from "~/types/public-page";
import { isValidEmailStrict } from "@donoor/validators";
import { allerAuPremierChampEnErreur } from "~/utils/validationChamps";

const props = defineProps<{
  modelValue: BuyerInfoModel;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: BuyerInfoModel): void;
  (e: 'back' | 'continue'): void;
}>();

const { t } = useI18n();

// ── Validation à la demande ────────────────────────────────────────────────
// Bouton JAMAIS désactivé : le clic valide, cerne de rouge, explique, défile.
// Règles identiques à l'adhésion — les deux parcours ne doivent pas diverger.
const erreurs = ref<Record<string, string>>({});
const aTente = ref(false);

function valider(): Record<string, string> {
  const b = props.modelValue;
  const e: Record<string, string> = {};
  if (!b.firstName?.trim()) e.firstName = t("billetterie.partage.erreurPrenomObligatoire");
  if (!b.lastName?.trim()) e.lastName = t("billetterie.partage.erreurNomObligatoire");
  if (!b.email?.trim()) {
    e.email = t("billetterie.partage.erreurEmailObligatoire");
  } else if (!isValidEmailStrict(b.email)) {
    e.email = t("billetterie.partage.erreurEmailInvalideExemple");
  }
  if (!b.confirmEmail?.trim()) {
    e.confirmEmail = t("billetterie.bslugStepCoordonnees.erreurConfirmezEmail");
  } else if (b.email?.trim() !== b.confirmEmail?.trim()) {
    e.confirmEmail = t("billetterie.bslugStepCoordonnees.erreurEmailsDifferents");
  }
  return e;
}

function auClicContinuer() {
  aTente.value = true;
  const e = valider();
  erreurs.value = e;
  if (Object.keys(e).length > 0) {
    allerAuPremierChampEnErreur(e, "payeur");
    return;
  }
  emit("continue");
}
</script>

<template>
  <div class="p-3 md:p-5">
    <div class="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-800">
      <UIcon name="i-lucide-info" class="size-4 text-[#717198]" />
      <i18n-t keypath="billetterie.partage.champsObligatoiresEtoile" tag="p" class="text-xs text-[#717198] dark:text-gray-500">
        <template #etoile>
          <span class="text-red-500">*</span>
        </template>
      </i18n-t>
    </div>

    <PublicPageBuyerInfoForm
      :erreurs="aTente ? erreurs : undefined"
      prefixe-id="payeur"
      variant="b"
      :model-value="modelValue"
      :title="t('billetterie.bslugStepCoordonnees.titreInformationsPaiement')"
      :subtitle="t('billetterie.bslugStepCoordonnees.sousTitreInformationsPaiement')"
      @update:model-value="$emit('update:modelValue', $event)"
    />

    <div class="flex items-center justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
      <button class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#1a1a1a] hover:bg-gray-200 dark:hover:bg-[#262626] transition-colors" @click="$emit('back')">
        <UIcon name="i-lucide-arrow-left" class="size-4" /> {{ t('commun.actions.retour') }}
      </button>
      <button data-testid="btn-step3-continue" class="px-8 py-3 accent-btn rounded-xl text-sm font-semibold transition-colors" @click="auClicContinuer">
        {{ t('commun.actions.continuer') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
